import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPagedResponse } from "../components/shared/Tables/PagedTable";
import { PageMode } from "../models/PagedTable";
import { IPart } from "../models/Part";

// Hits the existing paged-stock endpoint with pageSize=1 so the response only
// carries the `total` we need for each tab tile — the one-row row payload is a
// cheap way to avoid waiting on a dedicated stats endpoint.
function fetchCount(
  post: ReturnType<typeof useApiHelper>["post"],
  pageMode: PageMode
) {
  return post<{ searchBody: Record<string, unknown> }, IPagedResponse<IPart>>(
    `/part/stock/qc/1?pageMode=${pageMode}&pageSize=1`,
    { searchBody: {} }
  );
}

export default function useStockQcStats() {
  const { post } = useApiHelper();
  const staleTime = 1000 * 60 * 2;

  const qcCount = useQuery({
    queryKey: ["stockQcStats", "qc"],
    queryFn: () => fetchCount(post, PageMode.Stock),
    staleTime,
    refetchOnWindowFocus: false,
    retry: false,
    keepPreviousData: true,
  });

  const deletedCount = useQuery({
    queryKey: ["stockQcStats", "deleted"],
    queryFn: () => fetchCount(post, PageMode.DeletedStock),
    staleTime,
    refetchOnWindowFocus: false,
    retry: false,
    keepPreviousData: true,
  });

  const scrapOutCount = useQuery({
    queryKey: ["stockQcStats", "scrapOut"],
    queryFn: () => fetchCount(post, PageMode.ScrapOutStock),
    staleTime,
    refetchOnWindowFocus: false,
    retry: false,
    keepPreviousData: true,
  });

  return {
    qcCount,
    deletedCount,
    scrapOutCount,
    totals: {
      qc: qcCount.data?.data?.total,
      deleted: deletedCount.data?.data?.total,
      scrapOut: scrapOutCount.data?.data?.total,
    },
    isLoading:
      qcCount.isLoading || deletedCount.isLoading || scrapOutCount.isLoading,
  };
}
